import { memo, type CSSProperties, type ReactNode } from 'react';

import { getPinnedOffset } from '../../core/pinned-offset';
import type { DataGridDensity, RowData, DataGridCell as TanStackCell } from '../../types';
import type { DataGridVariant } from '../data-grid';
import { cellContentVariants, cellVariants } from '../data-grid.variants';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '../shared';
import { CellErrorBoundary } from './cell-error-boundary';

/**
 * The row's disclosure toggle, on the one cell in the row that renders it.
 *
 * `DataGridCell` renders it on the strength of this prop being present and on nothing else — never
 * on `cell.column.id === 'expand'`. Which cell holds the toggle is the row's decision, and it
 * differs by affordance: the `expand` system column, or the clamped column itself. Branching on an
 * id here would put knowledge of a specific column into the one component that has none.
 */
export interface CellExpansion {
  expanded: boolean;
  /** Already resolved against the row — this component has no notion of which row it is in. */
  label: string;
  onToggle: () => void;
}

export interface DataGridCellProps<TRow extends RowData> {
  cell: TanStackCell<TRow>;
  density?: DataGridDensity;
  variant?: DataGridVariant;
  /** A snapshot of the row's selection state, same as `DataGridRow` takes — needed here only to
   * paint a pinned cell's own background, since it sits on a sticky layer above the row rather
   * than showing the row's background through it (see `cellVariants`'s `pinned`/`selected`
   * compound variant). */
  selected?: boolean;
  /** Visible columns, their pinning, and the pinned ones' widths, as one string. A memo token,
   * never rendered. */
  columnLayoutKey: string;
  /**
   * Drops this cell's line clamp, which is the whole of what expanding a row does: a numerically
   * clamped column already resolves to `cellContentVariants`' `wrap: 'normal'`, so the clamp is
   * carried entirely by the inline `-webkit-line-clamp` style below.
   */
  rowExpanded?: boolean;
  /** Present on one cell per row — see `CellExpansion`. */
  expansion?: CellExpansion;
}

interface ResolvedCellContentProps<TRow extends RowData> {
  cell: TanStackCell<TRow>;
}

// A distinct component, not a value computed inline in `DataGridCell`'s own body: a throw has to
// happen during *this* component's render for `CellErrorBoundary`, wrapped around it below rather
// than around an already-computed value, to have anything to catch.
function ResolvedCellContent<TRow extends RowData>({ cell }: ResolvedCellContentProps<TRow>) {
  const cellDef = cell.column.columnDef.cell;
  // A plain string or number column has no `cell` at all — TanStack renders the accessor value
  // as-is, which here just means falling through to the raw value.
  const content: ReactNode =
    typeof cellDef === 'function'
      ? (cellDef(cell.getContext()) as ReactNode)
      : (cell.getValue() as ReactNode);
  return content;
}

function DataGridCellImpl<TRow extends RowData>({
  cell,
  density,
  variant,
  selected,
  rowExpanded,
  expansion,
}: DataGridCellProps<TRow>) {
  const meta = cell.column.columnDef.meta;
  // A control has no text to clip, clamp or align, so it takes neither the wrap treatment nor the
  // line clamp below.
  const isControl = meta?.control === true;
  const wrap = meta?.wrap;
  // `wrap: 0` would otherwise disagree with itself below — a number, so truthy for the line-clamp
  // check, but falsy for the truncate/normal one — so both read off this one boolean instead.
  const isWrapping = wrap === true || typeof wrap === 'number';
  const lineClamp = typeof wrap === 'number' ? wrap : undefined;
  // Expanding a row means "show me this row's data", not "show me the one clamped column's data".
  // Dropping the clamp alone left every other cell on its default treatment — one line ending in
  // an ellipsis — so an expanded row still hid most of what it held. Expanded, a cell wraps
  // whatever it was going to clip, whether that was a clamp or the default truncation.
  const expanded = rowExpanded === true;

  // `-webkit-line-clamp` takes a caller-chosen line count, not one of a fixed few — an arbitrary
  // Tailwind class built from it wouldn't exist in the compiled stylesheet, since the scanner
  // that decides which utilities to generate never sees a literal `line-clamp-N` to find.
  const clampStyle: CSSProperties | undefined =
    isControl || lineClamp === undefined || expanded
      ? undefined
      : {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: lineClamp,
          overflow: 'hidden',
        };

  // Keyed on the cell's own current value: a renderer that threw for one value and would render
  // fine for the next gets a fresh boundary instance (and so a fresh `hasError: false`) the moment
  // the underlying data actually changes, instead of staying stuck showing the fallback forever —
  // there is otherwise no reset path once `getDerivedStateFromError` has fired once for a `<td>`
  // React keeps reusing across renders (same `cell.id`, see `data-grid-row.tsx`).
  //
  // The type is part of the key, not just the printed form. `String()` alone maps `0` and `'0'`,
  // and `undefined` and `'undefined'`, onto one key — and a renderer that throws on one of a pair
  // and not the other is precisely a renderer that branches on type, which is the case most likely
  // to hit this. Prefixing `typeof` costs nothing and removes that collision class entirely.
  const cellValue = cell.getValue();
  const errorBoundaryResetKey = `${typeof cellValue}:${String(cellValue)}`;

  const content = (
    <span
      className={cellContentVariants({
        wrap: isControl ? 'control' : expanded || isWrapping ? 'normal' : 'truncate',
        align: meta?.align ?? 'left',
      })}
      style={clampStyle}
    >
      <CellErrorBoundary key={errorBoundaryResetKey}>
        <ResolvedCellContent cell={cell} />
      </CellErrorBoundary>
    </span>
  );

  const pinned = cell.column.getIsPinned();
  const pinnedOffset = getPinnedOffset(cell.table, cell.column.id, pinned);
  // No width: the `<colgroup>` declares it once for the whole column (see `data-grid.tsx`). Only
  // the sticky offset is per-cell, because it depends on what is pinned beside it.
  const style: CSSProperties =
    pinnedOffset === undefined ? {} : { [pinned === 'start' ? 'left' : 'right']: pinnedOffset };

  return (
    <td
      style={style}
      className={cellVariants({ align: meta?.align ?? 'left', density, variant, pinned, selected })}
    >
      {meta?.showTooltip ? (
        // The renderer runs a second time here — but only once the tooltip actually opens: Radix's
        // `TooltipContent` is `Presence`-gated and isn't mounted (`forceMount` is never passed)
        // until then, so this is not a per-render cost for every row, only a one-off per hover.
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <CellErrorBoundary key={errorBoundaryResetKey}>
              <ResolvedCellContent cell={cell} />
            </CellErrorBoundary>
          </TooltipContent>
        </Tooltip>
      ) : (
        content
      )}
      {/* After the content, not inside it: the clamp box has `overflow: hidden`, so a control
          within it would be the first thing clipped. After it and not before, because the content
          span is the `<td>`'s first element child and the truncate/wrap tests reach it that way.

          `size="icon"` needs no override here — the expand column is measured from
          `--control-height` plus the cell's padding, exactly like the actions column, so this is
          the step that fits. */}
      {expansion === undefined ? null : (
        <Button
          variant="ghost"
          size="icon"
          icon={expansion.expanded ? 'chevron-down' : 'chevron-right'}
          // The same right-then-down pair the group band uses, which is what the reference screen
          // shows at the row's end. They cannot be confused on one row: a band carries no
          // expansion toggle, so the band's chevron and this one never appear on the same `<tr>`.
          aria-expanded={expansion.expanded}
          aria-label={expansion.label}
          onClick={expansion.onToggle}
        />
      )}
    </td>
  );
}

// Unmemoized, every cell re-rendered whenever its row did — and a row re-renders when the roving
// tabindex moves onto or off it, which means arrowing down a list re-invoked the consumer's own
// cell renderer for every cell of two rows per keypress. That renderer is the expensive part and
// the one this package does not control.
//
// `cell` is compared by identity, not by reading getters off it. A TanStack cell is a live handle:
// `prev` and `next` point at the same object, so `prev.cell.getValue()` and `next.cell.getValue()`
// both return the value as it is *now* and can never differ. A comparator written that way reports
// "equal" for every change it was meant to catch — cells stopped picking up column pinning, which
// is how this was found. A new row model produces new cell objects, so identity is the honest test.
//
// Pinning is what changes with the cell object intact, since it lives in table state rather than in
// the row; `columnLayoutKey` carries it, the same token `DataGridRow` already compares. That token
// also carries the pinned columns' widths, because this cell's own sticky offset is a sum of them —
// an unpinned column's width is not here at all, since the `<colgroup>` owns it.
function propsAreEqual<TRow extends RowData>(
  prev: DataGridCellProps<TRow>,
  next: DataGridCellProps<TRow>,
): boolean {
  return (
    prev.cell === next.cell &&
    prev.columnLayoutKey === next.columnLayoutKey &&
    prev.selected === next.selected &&
    prev.density === next.density &&
    prev.variant === next.variant &&
    prev.rowExpanded === next.rowExpanded &&
    // By value, never by reference: `expansion` is rebuilt every render (its `onToggle` closes over
    // the row), so a reference check would re-render this cell on every render of the body and
    // defeat the memo — the same reason `data-grid-group-row.tsx` compares `selection?.checked`.
    prev.expansion?.expanded === next.expansion?.expanded &&
    prev.expansion?.label === next.expansion?.label
  );
}

export const DataGridCell = memo(DataGridCellImpl, propsAreEqual) as typeof DataGridCellImpl;
